import { useMemo } from "react";
import { useLeadStore, useLeaseCompStore, useLocationStore, usePipedriveStore, useSaleCompStore, useTenantInMarketStore } from "..";
import IPipedriveOrganization from "../interfaces/IPipedriveOrganization";
import useTenantsInMarketWithGlobalFilters from "../interfaces/useTenantsInMarketWithGlobalFilters";
import useDealsWithFocusArea from "./useDealsWithFocusArea";
import useDealsWithGlobalFilters from "./useDealsWithGlobalFilters";
import useFocusShape from "./useFocusShape";
import useLeadsWithFocusArea from "./useLeadsWithFocusArea";
import useLeadsWithGlobalFilters from "./useLeadsWithGlobalFilters";
import useLeaseCompsWithFocusArea from "./useLeaseCompsWithFocusArea";
import useLeaseCompsWithGlobalFilters from "./useLeaseCompsWithGlobalFilters";
import useLocationsWithFocusArea from "./useLocationsWithFocusArea";
import useSaleCompsWithFocusArea from "./useSaleCompsWithFocusArea";
import useSaleCompsWithGlobalFilters from "./useSaleCompsWithGlobalFilters";
import useTenantsInMarketWithFocusArea from "./useTenantsInMarketWithFocusArea";

export default function (organizations: IPipedriveOrganization[]): IPipedriveOrganization[] {
    const pipedriveStore = usePipedriveStore();
    const locationStore = useLocationStore();
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const focusShape = useFocusShape();
    const locations = useLocationsWithFocusArea(locationStore.locations);
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(pipedriveStore.deals));
    const leads = useLeadsWithFocusArea(useLeadsWithGlobalFilters(leadStore.leads));
    const leaseComps = useLeaseCompsWithFocusArea(useLeaseCompsWithGlobalFilters(leaseCompStore.leaseComps));
    const saleComps = useSaleCompsWithFocusArea(useSaleCompsWithGlobalFilters(saleCompStore.saleComps));
    const tenantsInMarket = useTenantsInMarketWithFocusArea(useTenantsInMarketWithGlobalFilters(tenantInMarketStore.tenantsInMarket));

    return useMemo(() => {
        if (!focusShape)
            return organizations;

        const referencedCompanyIds = new Set<number>(
            [
                ...locations.map(l => l.pipedriveCompanyId),
                ...deals.map(d => d.contactCompanyId),
                ...leads.map(l => l.pipedriveCompanyId),
                ...leaseComps.map(lc => lc.pipedriveCompanyId),
                ...saleComps.flatMap(sc => [sc.pipedriveBuyerId, sc.pipedriveSellerId, sc.pipedriveBrokerageId]),
                ...tenantsInMarket.map(tim => tim.pipedriveCompanyId),
            ].filter((id): id is number => id != null)
        );

        return organizations.filter(o => referencedCompanyIds.has(o.id));
    }, [focusShape, organizations, locations, deals, leads, leaseComps, saleComps, tenantsInMarket]);
}
